const GARMIN_CONNECT_ENDPOINT = '/api/garmin/connect';
const GARMIN_CONNECTION_STATUS_ENDPOINT = '/api/garmin/check_connection';

export type GarminConnectorOptions = {
  connectPath?: string;
  connectionStatusPath?: string;
};

export type GarminConnector = {
  connect: () => Promise<void>;
  cancel: () => void;
};

export function createGarminConnector(options: GarminConnectorOptions = {}): GarminConnector {
  const connectPath = options.connectPath || GARMIN_CONNECT_ENDPOINT;
  const connectionStatusPath = options.connectionStatusPath || GARMIN_CONNECTION_STATUS_ENDPOINT;

  let garminConnectWindow: Window | null = null;
  let checkConnectionInterval: number | null = null;
  let hasResolved = false;
  let resolveCallback: (() => void) | null = null;
  let rejectCallback: ((error: Error) => void) | null = null;

  const cleanup = () => {
    if (checkConnectionInterval) {
      window.clearInterval(checkConnectionInterval);
      checkConnectionInterval = null;
    }

    window.removeEventListener('message', handleOAuthMessage);

    if (garminConnectWindow && !garminConnectWindow.closed) {
      garminConnectWindow.close();
    }

    garminConnectWindow = null;
    resolveCallback = null;
    rejectCallback = null;
  };

  const resolveOnce = () => {
    if (hasResolved) return;
    hasResolved = true;
    const callback = resolveCallback;
    cleanup();
    callback?.();
  };

  const rejectOnce = (message: string) => {
    if (hasResolved) return;
    hasResolved = true;
    const callback = rejectCallback;
    cleanup();
    callback?.(new Error(message));
  };

  const handleOAuthMessage = (event: MessageEvent) => {
    if (!event.data || event.data.type !== 'garmin-connected') {
      return;
    }

    if (event.data.success) {
      resolveOnce();
    } else {
      const errorMessage = event.data.error || 'Failed to connect to Garmin. Please try again.';
      rejectOnce(errorMessage);
    }
  };

  const startCheckingConnection = () => {
    checkConnectionInterval = window.setInterval(async () => {
      try {
        const response = await fetch(connectionStatusPath);
        const data = await response.json();

        if (data.connected) {
          resolveOnce();
        } else if (!data.modal_connect_pending) {
          rejectOnce('Garmin connection was cancelled.');
        }
      } catch (error) {
        console.error('Error checking Garmin connection status:', error);
      }
    }, 2000);
  };

  const connect = () => {
    return new Promise<void>((resolve, reject) => {
      cleanup();
      hasResolved = false;
      resolveCallback = resolve;
      rejectCallback = reject;

      const fetchOAuthUrl = async () => {
        try {
          const response = await fetch(connectPath, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
            }
          });

          if (!response.ok) {
            rejectOnce('Failed to initialize Garmin connection. Please try again.');
            return;
          }

          const data = await response.json();

          if (!data.success || !data.oauth_url) {
            rejectOnce('Failed to initialize Garmin connection. Please try again.');
            return;
          }

          const width = 500;
          const height = 600;
          const left = (window.innerWidth - width) / 2;
          const top = (window.innerHeight - height) / 2;

          garminConnectWindow = window.open(
            data.oauth_url,
            'garminConnect',
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
          );

          if (!garminConnectWindow) {
            rejectOnce('Please enable popups to connect your Garmin account.');
            return;
          }

          window.addEventListener('message', handleOAuthMessage);
          startCheckingConnection();
        } catch (error) {
          console.error('Error starting Garmin OAuth flow:', error);
          rejectOnce('Failed to connect to Garmin. Please try again.');
        }
      };

      fetchOAuthUrl();
    });
  };

  const cancel = () => {
    cleanup();
  };

  return { connect, cancel };
}
